// React core and hooks for state management and lifecycle
import React, { useState, useEffect } from 'react';

// Bootstrap UI components for layout, modals, dropdowns, and notifications
import { Toast, ToastContainer, Button, Modal, Dropdown, DropdownButton } from 'react-bootstrap';

// Font Awesome for icon support in buttons and UI
import 'font-awesome/css/font-awesome.min.css';

import introJs from 'intro.js';

// Libraries for exporting table data to Excel, PDF, and printing
import jszip from 'jszip';
import pdfmake from 'pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';

// DataTables and plugins for advanced table features (sorting, filtering, exporting, etc.)
import DataTable from 'datatables.net-react';
import DataTablesCore from 'datatables.net-bs5';
import 'datatables.net-buttons-bs5';
import 'datatables.net-buttons/js/buttons.colVis.mjs';
import 'datatables.net-buttons/js/buttons.html5.mjs';
import 'datatables.net-buttons/js/buttons.print.mjs';
import 'datatables.net-colreorder-bs5';


// Utility functions for manipulating item names and handling form input
import { getPlainName, appendDetailsToItemName } from './FP_foodPantryUtils';
import { handleInputChange, handleAddNewItem } from './FP_foodPantryUtils';
import { useFoodPantryData } from './FP_foodPantryData.js';

// Table column configurations for inventory, transactions, and item details
import { columnsInventory, columnsTransactions, columnsItemDetails } from './FP_foodPantryTables';

// Modal components for receiving, releasing, viewing, and creating items
import { ReceiveItemsModal } from './modals/FP_ReceiveItemsModal';
import { ReleaseItemsModal } from './modals/FP_ReleaseItemsModal';
import { OtherTransactionsModal } from './modals/FP_OtherTransactionsModal';
import { ItemDetailsModal } from './modals/FP_ItemDetailsModal';
import { FakeItemDetailsModal } from './modals/FP_FakeItemDetailsModal';
import { CreateItemsAndVariationsModal } from './modals/FP_CreateItemsAndVariationsModal';

// Cookie library for session and user data management
import Cookies from 'js-cookie';

// Register DataTables plugins for export functionality
DataTablesCore.Buttons.jszip(jszip);
DataTablesCore.Buttons.pdfMake(pdfmake);
DataTable.use(DataTablesCore);

// MAIN FOOD PANTRY COMPONENT
function FoodPantry({ setContentOpen, userPermissions }) {
  // Custom hook provides all state and handlers for the food pantry dashboard
  const {
    inventory, // Array of inventory items for the selected organization
    setInventory, // Setter for inventory state
    transactions, // Array of transaction records
    organization, // Array of organizations available to the user
    selectedOrg, // Name of the currently selected organization
    selectedOrgID, // ID of the currently selected organization
    showOrgSelector, // Boolean to show/hide organization selector dropdown
    isRootUser, // Whether the current user is a root/super admin
    showTransactionReceiveModal, // Boolean to show/hide receive items modal
    setShowTransactionReceiveModal, // Setter for receive modal visibility
    showFakeItemDetailsModal,
    setShowFakeItemDetailsModal,
    showTransactionReleaseModal, // Boolean to show/hide release items modal
    setShowTransactionReleaseModal, // Setter for release modal visibility
    showTransactionOtherModal, // Boolean to show/hide other transactions modal
    setShowTransactionOtherModal, // Setter for other transactions modal visibility
    showItemDetailsModal, // Boolean to show/hide item details modal
    setShowItemDetailsModal, // Setter for item details modal visibility
    showCreationModal, // Boolean to show/hide create items/variations modal
    setShowCreationModal, // Setter for creation modal visibility
    handleOrgChange, // Handler for changing selected organization
    notifications, // Array of current notification banners
    setNotifications, // Setter for notifications array
    itemDetails, // Details of the currently selected item
    setItemDetails, // Setter for item details
    filteredItems, // Filtered list of items for display
    setFilteredItems // Setter for filtered items
  } = useFoodPantryData();

  // Shows a notification banner for 5 seconds with custom header, body, and type
  function showReceiveItemSuccessBanner(header, body, type) {
    const id = Date.now() + Math.random();
    setNotifications(prev => {
      let newQueue = prev.length >= 3 ? prev : prev;
      // If already 3, fade out the oldest
      if (prev.length >= 3) {
        const oldestId = prev[0].id;
        newQueue = prev.map((n, i) => i === 0 ? { ...n, fading: true } : n);
        setTimeout(() => {
          setNotifications(current => current.filter(n => n.id !== oldestId));
        }, 800); // match CSS fade duration
        newQueue = newQueue.slice(1); // will be removed after fade
      }
      return [...newQueue, { id, header, body, type, fading: false }];
    });
    setTimeout(() => {
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, fading: true } : n));
      setTimeout(() => {
        setNotifications(prev => prev.filter(n => n.id !== id));
      }, 800); // match CSS fade duration
    }, 5000);
  }
  // State for new item form input fields
  const [newItemDetails, setNewItemDetails] = useState({
    itemName: '',
    variationName: '',
    category: '',
    price: '',
    unit: '',
    pack: '',
    item_id: '',
    quantity: '',
    org_id: '',
    value: ''
  });

  // Handler for updating new item form fields
  const onInputChange = handleInputChange(setNewItemDetails);


  // Normal guided tour steps
  const guidedTourSteps = [
    {
      element: '[data-step="1"]',
      intro: "This is the Food Pantry Inventory, Here you can view, manage and create the items in your inventory.",
      hidePrev: true
    },
    {
      element: '[data-step="2"]',
      intro: "This is where you can view and manage the details of each item.",
      hidePrev: true
    },
    {
      element: '[data-step="3"]',
      intro: "These four Buttons are how you can receive inventory, release inventory and create new Items, Categories and Variations, as well as exporting the whole main table.",
      hidePrev: true
    }
  ];

  const hoverButtonSteps = [
    {
      element: '[data-step="receive-items"]',
      intro: "First we will take a tour on how to receive inventory, accessed by this button. (Click Next to continue the tour).",
      hidePrev: true
    },
    {
      element: '[data-step="release-items"]',
      intro: "Next we will take a tour on how to release inventory, accessed by this button. (Click Next to continue the tour).",
      hidePrev: true
    },
    {
      element: '[data-step="create-modal"]',
      intro: "Next we will take a tour on how to create new Items, Variations and Categories, accessed by this button. (Click Next to continue the tour).",
      hidePrev: true
    }
  ];

  const [foodPantryTourStager, setFoodPantryTourStager] = useState(false);

  // Add manualTour state
  const [manualTour, setManualTour] = useState(false);

  // Combined guided tour and receive items button tour logic
  useEffect(() => {
    if (!userPermissions) return;
    const fullTourNeeded = JSON.parse(localStorage.getItem("FullTourNeeded"));
    if (!fullTourNeeded) return;

    function areTourElementsReady() {
      const selectors = [
        '[data-step="1"]',
        '[data-step="2"]',
        '[data-step="3"]'
      ];
      return selectors.every(sel => document.querySelector(sel));
    }

    let pollInterval = null;
    function tryStartGuidedTour() {
      const introInstance = window.introJs && window.introJs();
      const isTourActive = introInstance && introInstance._currentStep !== undefined;
      if (areTourElementsReady() && !isTourActive) {
        clearInterval(pollInterval);
        const runningIntro = document.querySelector('.introjs-overlay');
        if (runningIntro) return;
        const intro = introJs();
        intro.setOptions({
          steps: guidedTourSteps,
          showBullets: false,
          showPreviousButton: false
        });
        setTimeout(() => {
          intro.start();
          intro.onbeforechange(function () {
            setTimeout(() => {
              const prevBtn = document.querySelector('.introjs-prevbutton');
              if (prevBtn) prevBtn.style.display = 'none';
              const nextBtn = document.querySelector('.introjs-nextbutton');
              if (nextBtn) nextBtn.textContent = 'Next';
              const doneBtn = document.querySelector('.introjs-donebutton');
              if (doneBtn) doneBtn.textContent = 'Next';
            }, 0);
          });
          intro.oncomplete(() => {
            // After guided tour, prompt/prepare the receive items button tour
            setFoodPantryTourStager("Receive");
          });
        }, 1000);
      }
    }

    pollInterval = setInterval(tryStartGuidedTour, 200);
    return () => clearInterval(pollInterval);
  }, [userPermissions, setFoodPantryTourStager]);

  useEffect(() => {
    if (foodPantryTourStager === "complete") {
      if (manualTour === true) {
        setManualTour(false);
        setFoodPantryTourStager("Manual Tour Complete");
      } else {
        // After completing the tour, redirect based on permissions
        if (userPermissions.includes("reports")) {
          setContentOpen("reports");
        } else {
          setContentOpen("searchOrganizations");
        }
      }
    }
  }, [foodPantryTourStager, userPermissions, setContentOpen]);
  

  useEffect(() => {
    if (foodPantryTourStager === "Receive") {
      // Highlight the receive button first
      const Intro = introJs();
      Intro.setOptions({
        steps: [hoverButtonSteps[0]], // Only the first step (receive button)
        exitOnOverlayClick: false,
        showBullets: false,
        showPreviousButton: false
      });
      setTimeout(() => {
        Intro.start();
        Intro.onbeforechange(function () {
          setTimeout(() => {
            const prevBtn = document.querySelector('.introjs-prevbutton');
            if (prevBtn) prevBtn.style.display = 'none';
            const nextBtn = document.querySelector('.introjs-nextbutton');
            if (nextBtn) nextBtn.textContent = 'Next';
            const doneBtn = document.querySelector('.introjs-donebutton');
            if (doneBtn) doneBtn.textContent = 'Next';
          }, 0);
        });
        Intro.oncomplete(() => {
          setShowTransactionReceiveModal(true);
        });
        Intro.onexit(() => {
          setShowTransactionReceiveModal(true);
        });
      }, 500);
    }
  }, [foodPantryTourStager, setShowTransactionReceiveModal]);

  useEffect(() => {
    if (foodPantryTourStager === "Release") {
      const Intro = introJs();
      Intro.setOptions({
        steps: [hoverButtonSteps[1]], // Only the second step
        exitOnOverlayClick: false,
        showBullets: false,
        showPreviousButton: false
      });
      setTimeout(() => {
        Intro.start();
        // Hide the back button for every step
        Intro.onbeforechange(function () {
          setTimeout(() => {
            const prevBtn = document.querySelector('.introjs-prevbutton');
            if (prevBtn) prevBtn.style.display = 'none';
            const nextBtn = document.querySelector('.introjs-nextbutton');
            if (nextBtn) nextBtn.textContent = 'Next';
            const doneBtn = document.querySelector('.introjs-donebutton');
            if (doneBtn) doneBtn.textContent = 'Next';
          }, 0);
        });
      }, 500);
      Intro.onComplete(() => {
        setFoodPantryTourStager("Release Modal");
        setShowTransactionReleaseModal(true);
      });
    }
  }, [foodPantryTourStager]);

  useEffect(() => {
    if (foodPantryTourStager === "Create Intro") {
      const Intro = introJs();
      Intro.setOptions({
        steps: [hoverButtonSteps[2]], // Only the third step
        exitOnOverlayClick: false,
        showBullets: false,
        showPreviousButton: true,
        hidePrev: true
      });
      setTimeout(() => {
        Intro.start();
        // Hide the back button for every step
        Intro.onbeforechange(function () {
          setTimeout(() => {
            const prevBtn = document.querySelector('.introjs-prevbutton');
            if (prevBtn) prevBtn.style.display = 'none';
            const nextBtn = document.querySelector('.introjs-nextbutton');
            if (nextBtn) nextBtn.textContent = 'Next';
            const doneBtn = document.querySelector('.introjs-donebutton');
            if (doneBtn) doneBtn.textContent = 'Next';
          }, 0);
        });
      }, 500);
      Intro.onComplete(() => {
        setFoodPantryTourStager("Create");
        setShowCreationModal(true);
      });
    }
  }, [foodPantryTourStager]);

  useEffect(() => {
    if (foodPantryTourStager === "Item Details Intro") {
      const Intro = introJs();
      Intro.setOptions({
        steps: [{
          element: '#inventory-table-container',
          intro: "This table displays the current inventory items. You can search, sort, and paginate through the items. Use the buttons above to manage your inventory.",
          hidePrev: true
        }],
        exitOnOverlayClick: false,
        showBullets: false
      });
      setTimeout(() => {
        Intro.start();
        // Hide the back button for every step
        Intro.onbeforechange(function () {
          setTimeout(() => {
            const prevBtn = document.querySelector('.introjs-prevbutton');
            if (prevBtn) prevBtn.style.display = 'none';
            const nextBtn = document.querySelector('.introjs-nextbutton');
            if (nextBtn) nextBtn.textContent = 'Next';
            const doneBtn = document.querySelector('.introjs-donebutton');
            if (doneBtn) doneBtn.textContent = 'Next';
          }, 0);
        });
      }, 500);
      Intro.onComplete(() => {
        setFoodPantryTourStager("Item Details");
        setShowFakeItemDetailsModal(true);
      });
    }
  }, [foodPantryTourStager, setShowFakeItemDetailsModal]);

  //exitOnOverlayClick: false
  //showbullets: false
  //
  // Render the main dashboard UI
  return (
    <React.Fragment>
      {/* Stacked Toast notification banners */}
      <ToastContainer className="toast-banner-container" position="top-end" style={{ zIndex: 9999 }}>
        {notifications.map((n) => (
          <Toast
            key={n.id}
            show={true}
            onClose={() => {
              setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, fading: true } : x));
              setTimeout(() => {
                setNotifications(prev => prev.filter(x => x.id !== n.id));
              }, 800);
            }}
            animation={true}
            className={`toast-banner rounded-4 toast-banner-${n.type?.toLowerCase()}${n.fading ? ' fade-out' : ''}`}
            onTouchStart={e => (e.currentTarget.swipeStartX = e.touches[0].clientX)}
            onTouchEnd={e => {
              const swipeEndX = e.changedTouches[0].clientX;
              const swipeStartX = e.currentTarget.swipeStartX;
              if (swipeStartX && Math.abs(swipeEndX - swipeStartX) > 80) {
                setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, fading: true } : x));
                setTimeout(() => {
                  setNotifications(prev => prev.filter(x => x.id !== n.id));
                }, 800);
              }
            }}
          >
            <Toast.Header closeButton>
              <img
                src={
                  localStorage.getItem("BurchCoin") === JSON.stringify(true)
                    ? './images/Burchcoin.png'
                    : './images/BridgingHopeNoWord.svg'
                }
                className="rounded me-2"
                alt="Notification Icon"
                style={{ width: '30px', height: '30px' }}
              />
              <strong className="me-auto">{n.header}</strong>
              <small>{/*A Few Seconds Ago*/}</small>
            </Toast.Header>
            <Toast.Body>
              {n.body}
            </Toast.Body>
          </Toast>
        ))}
      </ToastContainer>

      {/* Inventory card displays the main inventory table and organization selector */}
      <div className="card col-12 col-md-10 offset-0 offset-md-1 mt-5 card-body container my-3">
        <div className="card-header d-flex flex-wrap justify-content-between align-items-center pb-3"
          data-step="1">
          <h3>
            <span>
              <i className="bi bi-basket pe-3"></i>
              Food Pantry Inventory
              <i
                className="bi bi-question-circle d-none d-md-inline"
                style={{
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#4d59df',
                  marginRight: '0.75rem',
                  marginLeft: '0.25rem'
                }}
                onClick={() => {
                  setManualTour(true);
                  const intro = introJs();
                  intro.setOptions({
                    steps: guidedTourSteps,
                    showBullets: false,
                    showPreviousButton: false
                  });
                  setTimeout(() => {
                    intro.start();
                    intro.onbeforechange(function () {
                      setTimeout(() => {
                        const prevBtn = document.querySelector('.introjs-prevbutton');
                        if (prevBtn) prevBtn.style.display = 'none';
                        const nextBtn = document.querySelector('.introjs-nextbutton');
                        if (nextBtn) nextBtn.textContent = 'Next';
                        const doneBtn = document.querySelector('.introjs-donebutton');
                        if (doneBtn) doneBtn.textContent = 'Next';
                      }, 0);
                    });
                    intro.oncomplete(() => {
                      setFoodPantryTourStager("Receive");
                    });
                  }, 300);
                }}
              />
            </span>
          </h3>

          {/* Organization selector dropdown, shown if multiple organizations available */}
          {isRootUser ? (
            <select
              id="organization-select"
              className="form-select mt-3"
              style={{ width: '250px' }}
              value={selectedOrg}
              onChange={handleOrgChange}
              required
            >
              <option value="" disabled>Select Organization</option>
              {organization && organization.length > 0 && organization.map((org, index) => (
                <option key={index} value={org.name || ''}>
                  {org.name || 'Unknown Organization'}
                </option>
              ))}
            </select>
          ) : (
            <div className="d-flex align-items-center p-2 rounded border bg-light mt-3">
              <h5 className="mb-0">
                <span className="fw-bold">Organization:</span> {selectedOrg}
              </h5>
            </div>
          )}
          {/*
          <Button
            variant='primary'
            onClick={() => showReceiveItemSuccessBanner("A New Notification", "Hello?", "primary")}
            className="ms-3"
          >
            Show Notification Banner
          </Button>
          <Button
            variant='success'
            onClick={() => showReceiveItemSuccessBanner("Success!", "Operation completed successfully.", "success")}
            className="ms-2"
          >
            Show Success Banner
          </Button>
          <Button
            variant='danger'
            onClick={() => showReceiveItemSuccessBanner("Extreme Error!", "A critical error occurred.", "extreme-error")}
            className="ms-2"
          >
            Show Extreme Error Banner
          </Button>
          <Button
            variant='warning'
            onClick={() => showReceiveItemSuccessBanner("Alert!", "Please check your input.", "alert")}
            className="ms-2"
          >
            Show Alert Banner
          </Button>
        */}
        </div>

        {/* Inventory DataTable with export, receive, release, and create buttons */}
        <div className="card-body text-dark" id="inventory-table-container"

          data-step="2">
          <DataTable
            data={inventory}
            columns={columnsInventory}
            options={{
              paging: true, // Enable pagination
              pageLength: 12, // Show 12 items per page
              searching: true, // Enable search/filter
              ordering: true, // Enable column sorting
              scrollX: true,
              scrollCollapse: true,
              deferRender: true,      // faster initial render for large data
              responsive: false,
              dom: 'Bfrtip', // Layout for buttons and table controls
              buttons: [
                // Button to open receive items modal
                {
                  text: '<i class="fa fa-arrow-down"></i> Receive',
                  className: 'btnReceiveItems',
                  action: function () {
                    setShowTransactionReceiveModal(true);
                  }
                },
                // Button to open release items modal
                {
                  text: '<i class="fa fa-arrow-up"></i> Release',
                  className: 'btnDanger btnReleaseItems',
                  action: function () {
                    setShowTransactionReleaseModal(true);
                  }
                },
                // Button to open create items/variations modal
                {
                  text: '<i class="fa fa-plus"></i> Create',
                  className: 'btn btnCreateItems',
                  action: function () {
                    setShowCreationModal(true);
                  }
                },
                // Export button group for Excel, PDF, and Print
                {
                  extend: 'collection',
                  text: 'Export',
                  buttons: [
                    {
                      extend: 'excel',
                      text: 'Export to Excel'
                    },
                    {
                      extend: 'pdf',
                      text: 'Export to PDF'
                    },
                    {
                      extend: 'print',
                      text: 'Print'
                    }
                  ]
                }
              ],
              language: {
                info: "_START_-_END_ of _TOTAL_ items", // Custom info text for pagination
                infoEmpty: "No data available", // Shown when no data is present
                infoFiltered: "(filtered from _MAX_ total items)", // Shown when table is filtered
              },
              columnDefs: [
                {
                  targets: [2, 3, 4, 6], // Hide columns by index (e.g. internal IDs)
                  visible: false,
                  searchable: false
                },
              ],
              // After table is initialized, add flex/gap classes and data-step to each button for Intro.js
              initComplete: function () {
                // preserve reference to the DataTable API
                const api = this.api && typeof this.api === 'function' ? this.api() : null;

                // Attach buttons styling and intro attributes 
                setTimeout(() => {
                  const buttonsContainer = document.querySelector('.dt-buttons');
                  if (buttonsContainer) {
                    buttonsContainer.classList.add('d-flex', 'gap-3');
                    // Add Intro.js attributes to the button container
                    buttonsContainer.setAttribute('data-step', '3');
                    // Add data-step to individual buttons for tour
                    const btns = buttonsContainer.querySelectorAll('button, .btn');
                    // Receive
                    const receiveBtn = Array.from(btns).find(btn => btn.innerHTML.includes('fa-arrow-down'));
                    if (receiveBtn) receiveBtn.setAttribute('data-step', 'receive-items');
                    // Release
                    const releaseBtn = Array.from(btns).find(btn => btn.innerHTML.includes('fa-arrow-up'));
                    if (releaseBtn) releaseBtn.setAttribute('data-step', 'release-items');
                    // Create
                    const createBtn = Array.from(btns).find(btn => btn.innerHTML.includes('fa-plus'));
                    if (createBtn) createBtn.setAttribute('data-step', 'create-modal');
                  }
                }, 0);

                if (!api) return;

                // Safe adjust helper: wrap in RAF and try/catch
                const safeAdjust = () => {
                  try {
                    if (typeof window.requestAnimationFrame === 'function') {
                      window.requestAnimationFrame(() => {
                        try { api.columns.adjust(); } catch (err) { /* swallow */ }
                      });
                    } else {
                      api.columns.adjust();
                    }
                  } catch (e) {
                    /* swallow */
                  }
                };

                // Debounce helper
                let resizeTimer = null;
                const scheduleAdjust = (delay = 100) => {
                  if (resizeTimer) clearTimeout(resizeTimer);
                  resizeTimer = setTimeout(() => {
                    safeAdjust();
                    resizeTimer = null;
                  }, delay);
                };

                // Use ResizeObserver on the table container, compare width to avoid needless adjusts
                const container = document.getElementById('inventory-table-container') || document.body;
                let lastWidth = container ? Math.round(container.getBoundingClientRect().width) : null;

                if (typeof ResizeObserver !== 'undefined') {
                  const ro = new ResizeObserver(entries => {
                    for (const entry of entries) {
                      const w = Math.round(entry.contentRect.width);
                      if (w !== lastWidth) {
                        lastWidth = w;
                        scheduleAdjust(120);
                      }
                    }
                  });
                  try { ro.observe(container); } catch (e) { /* ignore if observe fails */ }

                  // cleanup on destroy
                  api.on && api.on('destroy', () => {
                    try { ro.disconnect(); } catch (e) {}
                    if (resizeTimer) { clearTimeout(resizeTimer); resizeTimer = null; }
                  });
                } else {
                  // fallback: debounced window resize
                  const onWinResize = () => scheduleAdjust(150);
                  window.addEventListener('resize', onWinResize);
                  api.on && api.on('destroy', () => {
                    window.removeEventListener('resize', onWinResize);
                    if (resizeTimer) { clearTimeout(resizeTimer); resizeTimer = null; }
                  });
                }

                // initial adjust after init
                scheduleAdjust(50);
              },
            }}
          />
        </div>
      </div>

      {/* Modal for receiving new inventory items */}
      <ReceiveItemsModal
        show={showTransactionReceiveModal}
        onHide={() => setShowTransactionReceiveModal(false)}
        selectedOrgID={selectedOrgID}
        newItemDetails={newItemDetails}
        handleInputChange={onInputChange}
        handleAddNewItem={handleAddNewItem}
        setInventory={setInventory}
        setNewItemDetails={setNewItemDetails}
        setShowTransactionReceiveModal={setShowTransactionReceiveModal}
        setShowTransactionReleaseModal={setShowTransactionReleaseModal}
        setShowTransactionOtherModal={setShowTransactionOtherModal}
        appendDetailsToItemName={appendDetailsToItemName}
        inventory={inventory}
        showReceiveItemSuccessBanner={showReceiveItemSuccessBanner}
        foodPantryTourStager={foodPantryTourStager}
        setFoodPantryTourStager={setFoodPantryTourStager}
      />

      {/* Modal for releasing inventory items */}
      <ReleaseItemsModal
        show={showTransactionReleaseModal}
        onHide={() => setShowTransactionReleaseModal(false)}
        selectedOrgID={selectedOrgID}
        showReceiveItemSuccessBanner={showReceiveItemSuccessBanner}
        foodPantryTourStager={foodPantryTourStager}
        setFoodPantryTourStager={setFoodPantryTourStager}
      />

      {/* Modal for logging other inventory transactions */}
      <OtherTransactionsModal
        show={showTransactionOtherModal}
        onHide={() => setShowTransactionOtherModal(false)}
        newItemDetails={newItemDetails}
        handleInputChange={onInputChange}
        handleAddNewItem={handleAddNewItem}
      />

      {/* Modal for viewing and editing details of a specific item */}
      <ItemDetailsModal
        show={showItemDetailsModal}
        onHide={() => setShowItemDetailsModal(false)}
        itemDetails={itemDetails}
        filteredItems={filteredItems}
        selectedOrgID={selectedOrgID}
        transactions={transactions}
        setItemDetails={setItemDetails}
        setFilteredItems={setFilteredItems}
        setShowItemDetailsModal={setShowItemDetailsModal}
        showReceiveItemSuccessBanner={showReceiveItemSuccessBanner}
      />

      {/* Modal for creating new items, variations, and categories */}
      <CreateItemsAndVariationsModal
        show={showCreationModal}
        onHide={() => setShowCreationModal(false)}
        selectedOrgID={selectedOrgID}
        handleInputChange={onInputChange}
        showReceiveItemSuccessBanner={showReceiveItemSuccessBanner}
        foodPantryTourStager={foodPantryTourStager}
        setFoodPantryTourStager={setFoodPantryTourStager}
      />

      {/* Fake Modal for Touring ItemDetails Modal */}
      <FakeItemDetailsModal
        show={showFakeItemDetailsModal}
        onHide={() => setShowFakeItemDetailsModal(false)}
        foodPantryTourStager={foodPantryTourStager}
        setFoodPantryTourStager={setFoodPantryTourStager}
      />
    </React.Fragment >
  );
}
export default FoodPantry;
